import React from 'react';

/**
 * MetricTile Component
 * 
 * Reusable metric card component used throughout the dashboard.
 * Features:
 * - Click to select/deselect
 * - Visual feedback with ring effect when selected
 * - Gradient backgrounds
 * - Icon and large count display
 * - Label and description text
 * 
 * @param {Object} metric - Metric data { count, label, description, color, icon }
 * @param {string} metricKey - Unique identifier for this metric
 * @param {boolean} isSelected - Whether this metric is currently selected
 * @param {Function} onSelect - Callback when metric is clicked
 * @param {string} testId - data-testid for testing
 */
const MetricTile = ({ metric, metricKey, isSelected, onSelect, testId }) => {
  const Icon = metric?.icon;
  
  // Defensive check: if no icon or metric, return null to prevent crashes
  if (!metric) {
    console.warn(`MetricTile: Missing metric for key "${metricKey}"`);
    return null;
  }
  
  // Generate color classes based on the metric's color
  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        gradient: 'from-blue-50 to-blue-100',
        border: 'border-blue-200',
        text: 'text-blue-600',
        hover: 'hover:shadow-lg hover:scale-[1.02]'
      },
      green: {
        gradient: 'from-green-50 to-green-100',
        border: 'border-green-200',
        text: 'text-green-600',
        hover: 'hover:shadow-lg hover:scale-[1.02]'
      },
      purple: {
        gradient: 'from-purple-50 to-purple-100',
        border: 'border-purple-200',
        text: 'text-purple-600',
        hover: 'hover:shadow-lg hover:scale-[1.02]'
      },
      orange: {
        gradient: 'from-orange-50 to-orange-100',
        border: 'border-orange-200',
        text: 'text-orange-600',
        hover: 'hover:shadow-lg hover:scale-[1.02]'
      },
      indigo: {
        gradient: 'from-indigo-50 to-indigo-100',
        border: 'border-indigo-200',
        text: 'text-indigo-600',
        hover: 'hover:shadow-lg hover:scale-[1.02]'
      },
      teal: {
        gradient: 'from-teal-50 to-teal-100',
        border: 'border-teal-200',
        text: 'text-teal-600',
        hover: 'hover:shadow-lg hover:scale-[1.02]'
      },
      red: {
        gradient: 'from-red-50 to-red-100',
        border: 'border-red-200',
        text: 'text-red-600',
        hover: 'hover:shadow-lg hover:scale-[1.02]'
      },
      rose: {
        gradient: 'from-rose-50 to-rose-100',
        border: 'border-rose-200',
        text: 'text-rose-600',
        hover: 'hover:shadow-lg hover:scale-[1.02]'
      },
      pink: {
        gradient: 'from-pink-50 to-pink-100',
        border: 'border-pink-200',
        text: 'text-pink-600',
        hover: 'hover:shadow-lg hover:scale-[1.02]'
      }
    };
    
    return colorMap[color] || colorMap.blue;
  };
  
  const colors = getColorClasses(metric.color);
  
  return (
    <div
      onClick={() => onSelect(metricKey)}
      className={`cursor-pointer bg-gradient-to-br ${colors.gradient} border-2 ${
        isSelected ? colors.border + ' ring-4 ring-' + metric.color + '-200' : colors.border
      } rounded-lg p-6 transition-all ${colors.hover}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        {Icon && <Icon className={`h-8 w-8 ${colors.text}`} />}
        {!Icon && <div className={`h-8 w-8 ${colors.text}`}></div>}
        <span className={`text-4xl font-bold ${colors.text}`}>
          {metric.count || 0}
        </span>
      </div>
      <p className="text-sm font-medium text-gray-700">{metric.label}</p>
      <p className="text-xs text-gray-500 mt-1">{metric.description}</p>
      <p className="text-xs text-gray-400 mt-1">Click to view details</p>
    </div>
  );
};

export default MetricTile;
